import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function HowToGetHo4ArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'How to Get HO-4 / Renters Insurance | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn how to get HO-4 renters insurance step by step, what information you need, and how to compare quotes quickly before move-in.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/how-to-get-ho4-renters-insurance"
          onNavigate={(href) => navigate(href)}
        />

        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => navigate('/knowledge-base')}
              style={{ marginBottom: '1rem' }}
            >
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>How to get HO-4</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              A quick step-by-step guide to getting renters insurance before your move-in date.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>
              Last modified: {HO4_LAST_MODIFIED}
            </p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                To get HO-4 renters insurance, gather your rental details, choose coverage limits, compare quotes, and
                start your policy before move-in. Most people can finish this in under 15 minutes when they have their
                address and lease requirements ready. If you want a refresher first, read{' '}
                <Link to="/knowledge-base/what-is-ho4-renters-insurance" style={articleLinkStyle}>
                  what HO-4 renters insurance is
                </Link>
                .
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What you should have ready first</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Property address and unit number.</li>
                <li style={{ marginBottom: '0.5rem' }}>Move-in date and lease requirements.</li>
                <li style={{ marginBottom: '0.5rem' }}>Estimated value of your personal belongings.</li>
                <li>Preferred deductible and liability limit range.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Step-by-step: how to buy HO-4</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Decide your personal property amount based on replacement cost, not original purchase price.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Choose a liability limit. Many renters compare $100K, $300K, and $500K. Use this guide on{' '}
                  <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>
                    how much liability you may need
                  </Link>{' '}
                  if you are unsure.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Pick a deductible that balances monthly premium and out-of-pocket cost.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Compare at least two quote options and review exclusions before binding.
                </li>
                <li>Set policy start date before move-in and provide proof of insurance to your landlord if needed.</li>
              </ol>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common delays to avoid</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Waiting until move-in day to start shopping.</li>
                <li style={{ marginBottom: '0.5rem' }}>Using liability limits below lease requirements.</li>
                <li>
                  Forgetting to list required interested party details for your property manager. If you are deciding
                  between policy types, review{' '}
                  <Link
                    to="/knowledge-base/how-is-ho4-renters-insurance-different-from-homeowners-insurance"
                    style={articleLinkStyle}
                  >
                    HO-4 vs homeowners insurance
                  </Link>
                  .
                </li>
              </ul>
            </div>
          </article>

          <section
            className="card mb-4"
            style={{
              borderColor: 'var(--color-green)',
              background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>
                Begin your HO-4 application in minutes and compare options that fit your apartment and budget.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/qualify')}
                style={{ fontWeight: 700, paddingInline: '1.4rem' }}
              >
                Get started now
              </button>
            </div>
          </section>

          <section
            className="card"
            style={{
              borderColor: '#FBBF24',
              background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)',
            }}
          >
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Need help before you buy?</h3>
              <p style={{ marginBottom: '1rem' }}>
                Speak with an agent if you want help choosing limits or matching your lease requirements.
              </p>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate('/help')}
                style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}
              >
                Speak to an agent
              </button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default HowToGetHo4ArticlePage;
